import React, { useEffect, useState, useRef } from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';
import { PostRequest } from 'components/common/index.js';

// Preview + confirm modal for a Starter / Business / Business Plus tier switch.
// Modelled on UpgradeToAnnualModal. An upgrade is charged immediately (prorated);
// a downgrade takes effect at the next renewal with no charge today — the copy
// switches on `direction`.
const ChangeTierModal = ({ targetTier, targetTierName, direction, onClose, onSuccess }) => {
  const [mode, setMode]       = useState('loading');
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState('');
  const [pending, setPending] = useState(false);
  const popupRef              = useRef();
  const nonceRef              = useRef(Math.random().toString(36).slice(2));

  const close = () => {
    if (popupRef.current) {
      popupRef.current.style.display = 'none';
    }
    if (onClose) {
      onClose();
    }
  };

  const fetchPreview = () => {
    PostRequest({
      url: '/charge/preview-change-tier',
      data: { target_tier: targetTier },
      callback: (data) => { setPreview(data); setMode('preview'); },
      422: (data) => { setError((data && data.error) || t('payment.upgrade_failed')); setMode('error'); }
    });
  };

  useEffect(() => { fetchPreview(); }, []);

  const confirm = () => {
    setPending(true);
    PostRequest({
      url: '/charge/change-tier',
      data: { target_tier: targetTier, nonce: nonceRef.current },
      callback: (data) => {
        setPending(false);
        if (data && data.ok) {
          setMode('success');
        } else {
          setError((data && data.error) || t('payment.upgrade_failed'));
          setMode('error');
        }
      },
      422: (data) => {
        setPending(false);
        setError((data && data.error) || t('payment.upgrade_failed'));
        setMode('error');
      }
    });
  };

  const loadingContent = (
    <div className="upgrade-to-annual-modal upgrade-to-annual-modal-loading" role="status">
      <div className="upgrade-to-annual-spinner" aria-hidden="true" />
    </div>
  );

  const isUpgrade = direction === 'upgrade';

  const previewContent = preview && (
    <div className="upgrade-to-annual-modal">
      <h3 className="upgrade-to-annual-modal-title">
        {(isUpgrade ? t('payment.change_tier_upgrade_title') : t('payment.change_tier_downgrade_title'))
          .replace('%{tier}', targetTierName)}
      </h3>

      <div className="upgrade-to-annual-modal-breakdown">
        <div className="upgrade-to-annual-modal-line">
          <span>{t('payment.change_tier_new_price_line')}</span>
          <span>{preview.new_price_formatted}</span>
        </div>
        <div className="upgrade-to-annual-modal-divider" />
        <div className="upgrade-to-annual-modal-line upgrade-to-annual-modal-total">
          <span>{t('payment.change_tier_charged_today_line')}</span>
          <span>{preview.charged_today_formatted}</span>
        </div>
      </div>

      <p className="upgrade-to-annual-modal-renewal">
        {(isUpgrade ? t('payment.change_tier_upgrade_effective_line') : t('payment.change_tier_downgrade_effective_line'))
          .replace('%{tier}', targetTierName)
          .replace('%{date}', preview.effective_date_formatted)}
      </p>

      <div className="upgrade-to-annual-modal-actions">
        <a className="btn upgrade-to-annual-modal-cancel" onClick={close} href="#">
          {t('payment.upgrade_to_annual_cancel_cta')}
        </a>
        <a
          className="btn subscription-card-action-primary"
          onClick={(event) => { event.preventDefault(); if (!pending) { confirm(); } }}
          href="#"
          aria-disabled={pending}
        >
          {pending ? t('sign_up.please_wait') : t('payment.change_tier_confirm_cta')}
        </a>
      </div>
    </div>
  );

  const successContent = (
    <div className="upgrade-to-annual-modal upgrade-to-annual-modal-success">
      <h3>{t('payment.change_tier_success_title')}</h3>
      <p>{isUpgrade ? t('payment.change_tier_upgrade_success_body') : t('payment.change_tier_downgrade_success_body')}</p>
      <div className="upgrade-to-annual-modal-actions">
        <a
          className="btn subscription-card-action-primary"
          onClick={(event) => { event.preventDefault(); if (onSuccess) { onSuccess(); } close(); }}
          href="#"
        >
          {t('payment.upgrade_to_annual_success_close_cta')}
        </a>
      </div>
    </div>
  );

  const errorContent = (
    <div className="upgrade-to-annual-modal upgrade-to-annual-modal-error">
      <h3>{t('payment.upgrade_to_annual_error_title')}</h3>
      <p>{error}</p>
      <div className="upgrade-to-annual-modal-actions">
        <a className="btn upgrade-to-annual-modal-cancel" onClick={close} href="#">
          {t('payment.upgrade_to_annual_cancel_cta')}
        </a>
      </div>
    </div>
  );

  let content = loadingContent;
  if (mode === 'preview') { content = previewContent; }
  if (mode === 'success') { content = successContent; }
  if (mode === 'error')   { content = errorContent; }

  return (
    <ContentPopup
      contextClass="wrapper"
      myRef={popupRef}
      autoOpen={true}
      content={content}
      cancelButton={false}
      beforeClose={close}
      notWide={true}
    />
  );
};

export default ChangeTierModal;
